/**
 * Snippets Store Types
 *
 * Defines the shape of the centralized snippets state for managing snippets data.
 * The `isLoaded` flag is crucial for preventing duplicate fetch requests -
 * once snippets are loaded, subsequent calls to fetchSnippets() will return
 * the cached data immediately.
 *
 * Error states are separated by operation type:
 * - `fetchError`: Set when fetching snippets fails
 * - `createError`: Set when creating a snippet fails
 */
import type { Snippet } from '../snippetTypes.ts';

export type SnippetsState = {
	/** Array of all snippets loaded from the server */
	snippets: Snippet[];
	/** True while a fetch or create operation is in progress */
	isLoading: boolean;
	/** Stores the most recent fetch error, null if no error */
	fetchError: Error | null;
	/** Stores the most recent create error, null if no error */
	createError: Error | null;
	/** True once snippets have been successfully loaded; prevents redundant fetches */
	isLoaded: boolean;
};

export const getInitialState = (): SnippetsState => ({
	snippets: [],
	isLoading: false,
	fetchError: null,
	createError: null,
	isLoaded: false,
});
